/**
 * Subscribers, the per-subscriber detail panel, and the SES suppression import.
 *
 * `Subscribers` is exported for `SuppressionImport.test.tsx` (#251) and
 * `SubscribersSuppression.test.tsx` (#346, #363, #364) — what the suppression
 * controls render, and for whom, is an RBAC decision made in this component.
 */
import { useEffect, useState } from "react";
import { useAsync } from "../useAsync.js";
import { can, type Grant } from "../rbac.js";
import { api, type SubscriberDetail, type SubscriberTimeline, type SuppressionCheckResult, type SuppressionImportReport } from "../api.js";
import { SkeletonCard, SkeletonTable } from "../Skeleton.js";
import { RefreshButton } from "../RefreshButton.js";
import { usePeople } from "../usePeople.js";

/**
 * The account-wide options are a confirmed, developer_admin-only action (#363):
 * the entry blocks the address for every newsroom on the deployment, is
 * mirrored to the SES account list, and nothing in the console removes it. The
 * server refuses anyone else; this is the warning before a real one.
 */
function confirmAccountWide(email: string, reason: "bounce" | "complaint"): boolean {
  return window.confirm(
    `Record a ${reason} for ${email}?\n\n` +
      "This is account-wide: it blocks mail to this address from every newsroom on the deployment, " +
      "not just this one, and is mirrored to the SES account suppression list.\n\n" +
      "It cannot be lifted from the console.\n\nContinue?",
  );
}

/** The message after a lift — never "lifted" alone while a global entry still blocks the address (#345). */
function liftMessage(email: string, stillSuppressedGlobally: boolean): string {
  return stillSuppressedGlobally
    ? `Org suppression lifted for ${email}, but the address is still suppressed account-wide by a bounce or complaint. No mail will reach it.`
    : `Lifted suppression for ${email}`;
}

/** The two account-wide reasons, offered only to a role the server will accept them from (#363). */
function SuppressReasonOptions({ accountWide }: { accountWide: boolean }) {
  return <>
    <option value="">Manual (this org only)</option>
    {accountWide && <option value="bounce">Bounce (account-wide, mirrored to SES)</option>}
    {accountWide && <option value="complaint">Complaint (account-wide, mirrored to SES)</option>}
  </>;
}

export function Subscribers({ org, grant, onViewSuppression }: { org: string; grant: Grant | null; onViewSuppression?: () => void }) {
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [rev, setRev] = useState(0);
  /** Cursor stack — one entry per page visited, so Back is exact rather than re-derived. */
  const [pages, setPages] = useState<(string | undefined)[]>([undefined]);
  const cursor = pages[pages.length - 1];
  const subs = useAsync(() => api.subscribers(org, query || undefined, cursor), [org, query, cursor, rev]);
  // The org-wide list is `suppression:manage` (developer_admin) on the server.
  // Requesting it for Editor or Support put a 403 in red on every visit (#346),
  // so for them it is never requested and the card is not drawn.
  const canManageSuppression = can(grant, "suppression:manage", org);
  const supps = useAsync(
    () => (canManageSuppression ? api.suppressions(org) : Promise.resolve(null)),
    [org, rev, canManageSuppression],
  );
  const [email, setEmail] = useState("");
  /** Empty stays manual/org-scoped exactly as before #247; bounce/complaint lands global (§4.13). */
  const [reason, setReason] = useState<"" | "bounce" | "complaint">("");
  const [msg, setMsg] = useState("");
  /** The subscriber whose detail panel is open (#205). */
  const [openSub, setOpenSub] = useState<string | null>(null);
  const [importReport, setImportReport] = useState<SuppressionImportReport | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const reload = () => setRev((n) => n + 1);

  const suppress = async () => {
    if (reason && !confirmAccountWide(email, reason)) return;
    setMsg("");
    try { await api.suppress(org, email, reason || undefined); setMsg(`Suppressed ${email}`); reload(); }
    catch (e) { setMsg(String(e)); }
  };
  const unsubscribeAll = async (sub: string, subEmail: string) => {
    // One click beside "Open" ended every subscription at once; ask first.
    if (!window.confirm(`Unsubscribe ${subEmail} from every newsletter?`)) return;
    setMsg("");
    try { await api.adminUnsubscribe(org, sub, subEmail); setMsg(`Unsubscribed ${subEmail} from all lists`); reload(); }
    catch (e) { setMsg(String(e)); }
  };
  const lift = async (liftEmail: string) => {
    setMsg("");
    try {
      const result = await api.unsuppress(org, liftEmail);
      setMsg(liftMessage(liftEmail, result.stillSuppressedGlobally));
      reload();
    }
    catch (e) { setMsg(String(e)); }
  };
  const runSuppressionImport = async (dryRun: boolean) => {
    setImportBusy(true); setMsg(""); setImportReport(null);
    try {
      setImportReport(await api.importSuppression(org, dryRun));
      if (!dryRun) reload();
    } catch (e) { setMsg(String(e)); } finally { setImportBusy(false); }
  };

  return (
    <div>
      <div className="pagehead">
        <div><h1>Subscribers</h1><p>The addressium subscriber record is the primary identity. Search, inspect, unsubscribe or suppress.</p></div>
        {/* Both reads: the table joins subscribers against the suppression list,
            so refreshing one of them would leave the Suppressed column stale. */}
        <RefreshButton
          refreshing={subs.refreshing || supps.refreshing}
          disabled={subs.loading}
          onClick={() => { void subs.refetch(); void supps.refetch(); }}
        />
      </div>
      {msg && <p className="muted">{msg}</p>}

      <div className="card">
        <div className="row">
          <input placeholder="Email starts with…" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setQuery(q.trim()); setPages([undefined]); } }} style={{ flex: 1 }} />
          <button className="btn" onClick={() => { setQuery(q.trim()); setPages([undefined]); }}>Search</button>
          {query && <button className="btn ghost" onClick={() => { setQ(""); setQuery(""); setPages([undefined]); }}>Clear</button>}
        </div>
        {/* Said out loud rather than left to be discovered. A substring match
            cannot use any index, and answering one meant loading the whole org
            into memory (#182) — so the search is a prefix, and the box says so. */}
        <p className="muted" style={{ margin: "6px 0 0", fontSize: 13 }}>
          Matches the START of an address. Results are paged.
        </p>
        {subs.loading && <SkeletonTable rows={6} />}
        {subs.error && <p className="err">{subs.error}</p>}
        {subs.data && subs.data.rows.length === 0 && <p className="muted">No subscribers match.</p>}
        {subs.data && subs.data.rows.length > 0 && (
          <table style={{ marginTop: 8 }}>
            <thead><tr><th>Email</th><th>Status</th><th>Entitlement</th><th>Last engaged</th><th></th></tr></thead>
            <tbody>
              {subs.data.rows.map((s) => (
                <tr key={s.sub}>
                  <td className="t-strong">{s.email}</td>
                  <td>{s.status}</td>
                  <td>{s.entitlement}</td>
                  <td className="muted">{s.lastEngagedAt ? new Date(s.lastEngagedAt).toLocaleString() : "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn ghost" onClick={() => setOpenSub(openSub === s.sub ? null : s.sub)}>
                      {openSub === s.sub ? "Close" : "Open"}
                    </button>{" "}
                    <button className="btn ghost" onClick={() => void unsubscribeAll(s.sub, s.email)}>Unsubscribe all</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {subs.data && (pages.length > 1 || subs.data.cursor) && (
          <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
            <button className="btn ghost" disabled={pages.length === 1}
              onClick={() => setPages(pages.slice(0, -1))}>Previous</button>
            <button className="btn ghost" disabled={!subs.data.cursor}
              onClick={() => setPages([...pages, subs.data!.cursor])}>Next</button>
            <span className="muted">Page {pages.length}</span>
          </div>
        )}
      </div>

      {openSub && <SubscriberPanel org={org} sub={openSub} grant={grant} onChanged={reload} />}

      <div className="card">
        <label>Manually suppress an address (does not delete)</label>
        <div className="row">
          <input placeholder="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <select aria-label="Suppression reason" value={reason} onChange={(e) => setReason(e.target.value as "" | "bounce" | "complaint")}>
            <SuppressReasonOptions accountWide={canManageSuppression} />
          </select>
          <button className="btn" onClick={() => void suppress()} disabled={!email}>Suppress</button>
        </div>
      </div>

      {/* The route was built, routed and IAM-granted with nothing calling it
          (#251). Gated on `suppression:manage` to match the server: these
          entries are global, written in bulk, and have no bulk way back, so the
          route is developer_admin-only and a button that 403s for everyone else
          would be worse than no button. */}
      {canManageSuppression && (
        <div className="card">
          <div className="muted" style={{ marginBottom: 8 }}>Import the SES account suppression list</div>
          <p className="muted" style={{ marginTop: 0 }}>
            The SES account list is copied in automatically every night at 02:00 UTC, once for
            every organization, so you do not need to run this before an import. Run it to pick
            up today&rsquo;s additions now instead of tonight. Imported addresses are blocked for
            every newsroom.
          </p>
          <div className="row">
            <button className="btn ghost" disabled={importBusy} onClick={() => void runSuppressionImport(true)}>
              {importBusy ? "Reading…" : "Dry run"}
            </button>
            <button className="btn" disabled={importBusy} onClick={() => void runSuppressionImport(false)}>
              Import
            </button>
          </div>
          {importReport && (
            <div style={{ marginTop: 8 }}>
              <p className="muted" style={{ margin: 0 }}>
                {importReport.dryRun ? "Dry run — nothing was written. " : ""}
                Read {importReport.read}, {importReport.dryRun ? "would write" : "wrote"}{" "}
                {importReport.written}
                {importReport.malformed > 0 ? `, ${importReport.malformed} with no usable address` : ""}.
              </p>
              {/* Listed, not counted. The alternative reading of "4 skipped" is
                  "4 addresses we will now mail", and that is the one that
                  matters — an unmapped reason is never written. */}
              {importReport.unmapped.length > 0 && (
                <>
                  <p className="err" style={{ marginBottom: 4 }}>
                    {importReport.unmapped.length} entr
                    {importReport.unmapped.length === 1 ? "y" : "ies"} carry a reason we do not map
                    and were NOT suppressed — these addresses stay mailable.
                  </p>
                  <table>
                    <thead><tr><th>Email</th><th>Reason</th></tr></thead>
                    <tbody>
                      {importReport.unmapped.map((u) => (
                        <tr key={u.email}>
                          <td className="t-strong">{u.email}</td>
                          <td className="muted">{u.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {canManageSuppression && <div className="card">
        <div className="muted" style={{ marginBottom: 8 }}>Suppression list</div>
        {/* This is the org partition only. The account-wide one can be tens of
            thousands of rows, so it is paged on the Suppression screen rather
            than duplicated here (#344). */}
        <p className="muted" style={{ marginTop: 0 }}>
          This org&rsquo;s own suppressions. Account-wide bounces, complaints and the SES import
          are listed on the{" "}
          {onViewSuppression
            ? <button className="btn ghost" onClick={onViewSuppression}>Suppression screen</button>
            : "Suppression screen"}.
        </p>
        {supps.loading && <SkeletonTable rows={4} />}
        {supps.error && <p className="err">{supps.error}</p>}
        {supps.data && supps.data.length === 0 && <p className="muted">No suppressed addresses.</p>}
        {supps.data && supps.data.length > 0 && (
          <table>
            <thead><tr><th>Email</th><th>Source</th><th>Scope</th><th>Added</th><th></th></tr></thead>
            <tbody>
              {supps.data.map((s) => (
                <tr key={`${s.email}:${s.scope}`}>
                  <td className="t-strong">{s.email}</td>
                  <td>{s.source}</td>
                  <td>{s.scope}</td>
                  <td className="muted">{new Date(s.addedAt).toLocaleString()}</td>
                  <td>
                    {s.scope === "org"
                      ? <button className="btn ghost" onClick={() => void lift(s.email)}>Lift</button>
                      : <span className="muted">global</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>}
    </div>
  );
}

/**
 * Subscriber detail: attributes, per-list opt-ins, segments (#205).
 *
 * The list view carries five fields. This is everything else — most importantly
 * the ATTRIBUTES, which are the merge-tag values every personalised send renders
 * from, and the PER-LIST status, which previously could only be changed
 * all-lists-at-once by "Unsubscribe all".
 */
function SubscriberPanel({ org, sub, grant, onChanged }: { org: string; sub: string; grant: Grant | null; onChanged: () => void }) {
  const who = usePeople(org);
  const [detail, setDetail] = useState<SubscriberDetail | null>(null);
  const [rows, setRows] = useState<Array<{ k: string; v: string }>>([]);
  const [timeline, setTimeline] = useState<SubscriberTimeline | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineMsg, setTimelineMsg] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  /** Local + live (SES) suppression status for this one address (#247). */
  const [check, setCheck] = useState<SuppressionCheckResult | null>(null);
  const [checkMsg, setCheckMsg] = useState("");
  const [reason, setReason] = useState<"" | "bounce" | "complaint">("");

  const load = (d: SubscriberDetail) => {
    setDetail(d);
    setRows(Object.entries(d.attributes).map(([k, v]) => ({ k, v })));
    setTimeline(null);
    setTimelineMsg("");
  };

  useEffect(() => {
    let live = true;
    setDetail(null); setMsg("");
    api.subscriber(org, sub).then((d) => live && load(d)).catch((e) => live && setMsg(String(e)));
    return () => { live = false; };
  }, [org, sub]);

  // Runs against `detail.email` (not `sub`) so a re-fetched detail — e.g. after
  // suppressing — re-checks under the same effect rather than needing a second
  // trigger wired in by hand.
  useEffect(() => {
    let live = true;
    setCheck(null); setCheckMsg("");
    if (!detail) return;
    api.suppressionCheck(org, detail.email).then((c) => live && setCheck(c)).catch((e) => live && setCheckMsg(String(e)));
    return () => { live = false; };
  }, [org, detail?.email]);

  // The check effect is keyed on the address, which a suppress or lift does not
  // change, so both re-check by hand — otherwise the panel keeps showing the
  // entry that was just removed, and its Lift button with it.
  const recheck = async (email: string) => setCheck(await api.suppressionCheck(org, email));

  const suppressThis = async () => {
    if (!detail) return;
    if (reason && !confirmAccountWide(detail.email, reason)) return;
    setBusy(true); setCheckMsg("");
    try {
      await api.suppress(org, detail.email, reason || undefined);
      load(await api.subscriber(org, sub));
      await recheck(detail.email);
      onChanged();
    } catch (e) { setCheckMsg(String(e)); }
    finally { setBusy(false); }
  };

  /**
   * Lift, here as well as on the list card (#346). The list is
   * developer_admin-only, and it held the console's only Lift button — so
   * Editor and Support, whom the server lets lift, could suppress someone and
   * had no way back.
   */
  const liftThis = async () => {
    if (!detail) return;
    setBusy(true); setCheckMsg("");
    try {
      const result = await api.unsuppress(org, detail.email);
      load(await api.subscriber(org, sub));
      setMsg(liftMessage(detail.email, result.stillSuppressedGlobally));
      await recheck(detail.email);
      onChanged();
    } catch (e) { setCheckMsg(String(e)); }
    finally { setBusy(false); }
  };

  const saveAttributes = async () => {
    setBusy(true); setMsg("");
    // Blank keys are dropped rather than rejected: an operator adds a row, then
    // decides against it, and a validation error for a row they never filled in
    // is noise. A duplicate key would silently win — flag it instead.
    const kept = rows.filter((r) => r.k.trim());
    const keys = kept.map((r) => r.k.trim());
    if (new Set(keys).size !== keys.length) {
      setMsg("Two attributes share a name — one would silently overwrite the other.");
      setBusy(false);
      return;
    }
    try {
      load(await api.setSubscriberAttributes(org, sub, Object.fromEntries(kept.map((r) => [r.k.trim(), r.v]))));
      setMsg("Attributes saved.");
      onChanged();
    } catch (e) { setMsg(String(e)); }
    finally { setBusy(false); }
  };

  const setStatus = async (listId: string, status: "confirmed" | "unsubscribed") => {
    // Manual confirmation bypasses double opt-in, so it is confirmed out loud
    // here AND demanded again by the server — a client-only check is not a
    // safeguard, it is a speed bump.
    if (status === "confirmed") {
      const ok = window.confirm(
        `Manually confirming ${detail?.email} on "${listId}" bypasses double opt-in.\n\n` +
          "The subscription will be recorded with basis \"manual_admin\" naming you, not as a real opt-in, " +
          "and the action is written to the audit log.\n\nContinue?",
      );
      if (!ok) return;
    }
    setBusy(true); setMsg("");
    try {
      load(await api.setSubscriptionStatus(org, sub, listId, status, status === "confirmed"));
      setMsg(`"${listId}" set to ${status}.`);
      onChanged();
    } catch (e) { setMsg(String(e)); }
    finally { setBusy(false); }
  };

  const loadTimeline = async () => {
    setTimelineLoading(true); setTimelineMsg("");
    try { setTimeline(await api.subscriberTimeline(org, sub)); }
    catch (e) { setTimelineMsg(String(e)); }
    finally { setTimelineLoading(false); }
  };

  const hasOrg = check?.local.some((e) => e.scope === "org") ?? false;
  const hasGlobal = check?.local.some((e) => e.scope === "global") ?? false;

  if (!detail) {
    return msg ? <div className="card muted"><span className="err">{msg}</span></div> : <SkeletonCard lines={4} label="Loading subscriber…" />;
  }

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <strong>{detail.email}</strong>
        <span className="muted">
          {detail.entitlement}
          {detail.lastEngagedAt ? ` · last engaged ${new Date(detail.lastEngagedAt).toLocaleDateString()}` : ""}
        </span>
      </div>
      <p className="muted" style={{ margin: "4px 0 0" }}>
        External customer ID: {detail.externalId ?? "not linked"}
      </p>
      {detail.suppressed && (
        // The single most useful line on this panel: every opt-in below is moot
        // while this is true, and an operator who cannot see it reads the next
        // send as broken.
        <p className="err" style={{ margin: "6px 0 0" }}>
          Suppressed — no send will reach this address, whatever the opt-ins below say.
          {hasGlobal
            ? " An account-wide bounce or complaint blocks it, and that cannot be lifted from the console."
            : hasOrg ? " Use “Lift suppression” below to change that." : ""}
        </p>
      )}
      {msg && <p className={msg.includes("saved") || msg.includes("set to") || msg.startsWith("Lifted") ? "muted" : "err"} style={{ margin: "6px 0 0" }}>{msg}</p>}

      <div style={{ marginTop: 16 }}>
        <strong>Suppression</strong>{" "}
        <span className="muted">local record, plus a live check against the SES account list (#247)</span>
        {checkMsg && <p className="err" style={{ margin: "6px 0 0" }}>{checkMsg}</p>}
        {!check && !checkMsg && <p className="muted" style={{ margin: "6px 0 0" }}>Checking…</p>}
        {check && (
          <div style={{ margin: "6px 0 0" }}>
            <p style={{ margin: 0 }}>
              Local: {check.local.length === 0
                ? <span className="muted">not suppressed here</span>
                : check.local.map((e) => `${e.source} (${e.scope})`).join(", ")}
            </p>
            <p style={{ margin: 0 }}>
              SES account list:{" "}
              {check.liveError
                ? <span className="err">could not check ({check.liveError})</span>
                : check.live === undefined
                  ? <span className="muted">not checked</span>
                  : check.live === null
                    ? <span className="muted">clear</span>
                    : <span className="err">suppressed — {check.live.reason}{check.live.at ? ` on ${new Date(check.live.at).toLocaleDateString()}` : ""}</span>}
            </p>
          </div>
        )}
        <div className="row" style={{ marginTop: 8 }}>
          <select aria-label="Reason for this address" value={reason} onChange={(e) => setReason(e.target.value as "" | "bounce" | "complaint")} disabled={busy}>
            <SuppressReasonOptions accountWide={can(grant, "suppression:manage", org)} />
          </select>
          <button className="btn ghost" disabled={busy} onClick={() => void suppressThis()}>Suppress this address</button>
          {/* Org entries only: lift never touches a global one (#345). */}
          {hasOrg && (
            <button className="btn ghost" disabled={busy} onClick={() => void liftThis()}>Lift suppression</button>
          )}
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <strong>Attributes</strong>{" "}
        <span className="muted">merge-tag values, e.g. {"{{first_name}}"}</span>
        <table style={{ marginTop: 6 }}>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={{ width: "35%" }}>
                  <input value={r.k} placeholder="first_name" disabled={busy} style={{ width: "100%" }}
                    onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, k: e.target.value } : x)))} />
                </td>
                <td>
                  <input value={r.v} placeholder="Ada" disabled={busy} style={{ width: "100%" }}
                    onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, v: e.target.value } : x)))} />
                </td>
                <td style={{ width: 1 }}>
                  <button className="btn ghost" disabled={busy}
                    onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button className="btn ghost" disabled={busy} onClick={() => setRows([...rows, { k: "", v: "" }])}>
            Add attribute
          </button>
          <button className="btn" disabled={busy} onClick={() => void saveAttributes()}>
            {busy ? "Saving…" : "Save attributes"}
          </button>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div>
            <strong>Engagement timeline</strong>{" "}
            <span className="muted">opens, clicks and delivery events recorded for this subscriber</span>
          </div>
          <button className="btn ghost" disabled={timelineLoading} onClick={() => void loadTimeline()}>
            {timelineLoading ? "Loading…" : timeline ? "Refresh" : "Load timeline"}
          </button>
        </div>
        {timelineMsg && <p className="err" style={{ margin: "6px 0 0" }}>{timelineMsg}</p>}
        {timeline && (timeline.events.length === 0 ? <p className="muted">No engagement events recorded.</p> : (
          <table style={{ marginTop: 8 }}>
            <thead><tr><th>When</th><th>Event</th><th>Campaign</th><th>Link</th></tr></thead>
            <tbody>{timeline.events.map((event, i) => (
              <tr key={`${event.campaignId}-${event.at}-${event.type}-${i}`}>
                <td>{new Date(event.at).toLocaleString()}</td>
                <td className="t-strong">{event.type}</td>
                <td>{event.subject} <span className="muted">({event.campaignId})</span></td>
                <td>{event.linkId ?? "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        ))}
        {timeline?.hasMore && <p className="muted">Showing the 100 most recent events.</p>}
      </div>

      <div style={{ marginTop: 16 }}>
        <strong>Lists</strong>
        <table style={{ marginTop: 6 }}>
          <thead><tr><th>Newsletter</th><th>Status</th><th>Consent</th><th></th></tr></thead>
          <tbody>
            {detail.lists.map((l) => (
              <tr key={l.listId}>
                <td>{l.name} <span className="muted">({l.listId})</span></td>
                <td className={l.status === "confirmed" ? "t-strong" : "muted"}>
                  {/* Spelled out so it is never read as the reader's own opt-out (#367). */}
                  {l.status === "sunset"
                    ? <span title="Dropped from this newsletter by the re-engagement sweep after the win-back emails went unclicked. Not an unsubscribe: the reader can re-subscribe.">sunset (inactive)</span>
                    : (l.status ?? "not subscribed")}
                </td>
                <td className="muted">
                  {/* Absent provenance is shown as unknown, never as consent. */}
                  {l.consent?.basis === "manual_admin"
                    ? `added by ${who(l.consent.actor) ?? "an admin"}`
                    : (l.consent?.basis ?? (l.status ? "unknown" : "—"))}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {l.status !== "confirmed" && (
                    <button className="btn ghost" disabled={busy} onClick={() => void setStatus(l.listId, "confirmed")}>
                      Confirm
                    </button>
                  )}{" "}
                  {l.status !== "unsubscribed" && l.status !== undefined && (
                    <button className="btn ghost" disabled={busy} onClick={() => void setStatus(l.listId, "unsubscribed")}>
                      Unsubscribe
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 16 }}>
        <strong>Segments</strong>{" "}
        <span className="muted">
          {/* Explicit cohorts only — see subscriberDetail: evaluating every rule
              in the org per detail view would be slow and would show an answer
              that changes without anyone editing anything. */}
          explicit cohorts (#203); rule-based segments are evaluated at send time
        </span>
        {detail.segments.length === 0 ? (
          <p className="muted" style={{ margin: "6px 0 0" }}>Not in any explicit cohort.</p>
        ) : (
          <p style={{ margin: "6px 0 0" }}>
            {detail.segments.map((s) => `${s.name} (${s.segmentId})`).join(", ")}
          </p>
        )}
      </div>
    </div>
  );
}
